////////////////////////////////////////////////////////////////////////////////
// Fetcher
////////////////////////////////////////////////////////////////////////////////

 /**
  * Make Fetch requests using the Promise methods wuth AJAX fallbacks
  *
  * @author Mark Notton <mark@marknotton.uk>
  *
  * @license Copyright 2019 Mark Notton
  *
  * @module Utilities
  *
  * @example Standard
    const fetcher = new Fetcher()

    fetcher.get('/end-points', (response) => {
      if ( response.success ) {
        console.log('Success:', response)
      } else {
        console.error('Failure:', response)
      }
    })
  *
  * @example Slimline. The 'success' property won't get passed into the type of response.
    fetcher.get('/end-points', {
      success : (response) => { console.log('success', response) },
      error   : (response) => { console.log('error', response) }
    })
  *
  * @example Fetching a template
    const settings = { template : '/news/_entry.twig', id : 38 }

    fetcher.get('/fetch-template', settings, {
      success : (response) => { console.log('success', response) },
      error   : (response) => { console.log('error', response) }
    })
  *
  * @example Manually abort requests
    fetcher.abort()
  *
  * Licensed under the Apache License, Version 2.0 (the "License");
  * you may not use this file except in compliance with the License.
  * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0
  *
  * Unless required by applicable law or agreed to in writing, software
  * distributed under the License is distributed on an "AS IS" BASIS,
  * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  * See the License for the specific language governing permissions and
  * limitations under the License.
  */

class Fetcher extends Utilities {

  constructor () {

    super()

    this.defaults = {
      url      : '/fetch-data',
      method   : 'POST',
      timeout  : 100000,
      request  : 'fetch',
      dataType : 'json',
      headers  : {
        'Content-Type' : 'application/json',
        'Accept' : 'application/json',
      }
		}

    // Add CSFR Token headers if they exist
    if ( typeof window.csrfTokenName !== 'undefined' && typeof window.csrfTokenValue !== 'undefined' ) {
      this.defaults.headers[window.csrfTokenName] = window.csrfTokenValue
    }

  }

  /** ==========================================================================
   * Get Method
   * ===========================================================================
   * A universal and independant method for managing all aspects of a fetch/ajax request.
   * @param  {Boolean}  request  'true' performs Fetch requests. 'false' performs AJAX requests
   * @param  {Function} callback Triggered after a success or error response is returned. A 'response' object is passed back
   * @param  {Number}   timeout  The amount of time in miliseconds that the request will abort
   * @param  {String}   url      The url for the POST/GET method
   * @param  {Object}   handlers If an object has `success` AND `error` keys, this will be assumed
   *                             to contain methods for their respective condition and will be triggered INSTEAD of the callback.
   * @param  {Object}   settings Settings can contain all of the above arguments.
   *                             And may also include other properties from the default object:
   *                             url, method, timeout, request, dataType, and headers.
   *                             More importantly any properties not in defaults will be passed into the request query.
   * @return {Object}
   */
  get (...settings) {

    this.settings = Object.assign({}, this.defaults);

    this.manage(settings, {
      boolean  : (setting) => { this.settings.request = setting},
      function : (setting) => { this.settings.callback = setting },
      number   : (setting) => { this.settings.timeout = setting },
      string   : (setting) => { this.settings.url = setting },
      object   : (setting) => {
        if ( Object.keys(setting).includes('success') || Object.keys(setting).includes('error') ) {
          this.settings.handlers = setting
        } else {
          this.settings = this.merge(this.defaults, setting, ['headers'])
        }
      }
    })

    // Using the defaults object keys as reference -- seperate any arguemnts
    // that don't belong in the settings and put them in their own data object.
    // These data elements will be passed into the controller request.

    let data = {}

    Object.entries(this.settings).forEach((entry) => {
      const [key, value] = entry;
      if ( !Object.keys(this.defaults).includes(key) && key !== 'callback' && key !== 'handlers') {
        data[key] = value;
        delete this.settings[key]
      }
    })

      this.settings.headers['X-Requested-With'] = 'fetch'
      this._fetch(data)
  }


  /** ==========================================================================
   * Fetch Request
   * ===========================================================================
   * @param  {Object} data A range of data that will be passed into the POST/GET method
   */
  async _fetch(data = {}) {

    const controller = new AbortController()
    const signal = controller.signal
    const options = {
      mode    : 'cors',
      method  : this.settings.method,
      headers : new Headers(this.settings.headers),
      body: JSON.stringify(data),
      credentials: 'same-origin'
    }

    // Add an abort method for Fetch requests -----------------------------------

    this.abort = (callback = false) => {
      controller.abort()
      if ( callback ) { callback() }
    }

    return await fetch(this.settings.url, { signal, ...options })
    .then(response => response.json())
    .then(this._success.bind(this))
    .catch(err => { this._error(err) })

  }

  /** ==========================================================================
   * Success and Error Handlers
   * ===========================================================================
   * Private handlers for reoccuring succes or errors methods
   */

  _success(response) {
    if (this.settings.handlers && this.settings.handlers.success) { this.settings.handlers.success(response) }
    else if (this.settings.callback) { response.success = true; this.settings.callback(response) }
    else { console.log(response) }
  }

  _error(response) {
    if (this.settings.handlers && this.settings.handlers.error) { this.settings.handlers.error(response) }
    else if (this.settings.callback) { this.settings.callback(response) }
    else { console.error(response) }
  }

};
